//AsistenciaABM_Dashboard
import React, { useEffect, useState, useContext, useRef } from "react";
import axiosInstance from "utils/axiosInstance";
import { AuthContext } from "context/AuthContext";
import { parse, format, formatISO } from "date-fns";
import Calendar from "react-calendar";
import { TbCalendarPlus } from "react-icons/tb";
import "react-calendar/dist/Calendar.css";
import "assets/css/MiniCalendar.css";
import { toast } from "react-toastify";
import { getLastThursday, hayEventoElUltimoJueves } from "utils/jueves";
import { irAlElemento } from "utils/scroll";
import Card from "components/card";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar as CalendarIcon } from "lucide-react";
import {
  Badge,
  Button,
  EmptyState,
  Pagination,
  SectionHeader,
  SelectorFecha,
  selectClassNames,
} from "components/ui";

// React Table
import {
  createColumnHelper,
  useReactTable,
  ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";

// Icons
import { FaInfoCircle, FaCheck } from "react-icons/fa";
import { MdKeyboardArrowDown } from "react-icons/md";
import { GiBarbecue, GiSoccerBall } from "react-icons/gi";

// 1) React Select Imports:
import Select, { SingleValue } from "react-select";

/** 
 * Options for "tipo". We'll default to "Fútbol". 
 */
interface EventTypeOption {
  value: string;
  label: string;
}

const eventTypeOptions: EventTypeOption[] = [
  { value: "Fútbol", label: "Fútbol" },
  { value: "Peña", label: "Peña" },
];

const formatDate = (isoDate: string) => {
  return format(new Date(isoDate), "dd/MM/yy");
};

// ============ STAGGER ANIMATION VARIANTS (asistentes) ============
const attendeeContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.025 } },
};
const attendeeItem = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.18 } },
};

interface Persona {
  id: number;
  nombre: string;
}

interface Asistencia {
  id: number;  // ID del evento
  fecha: string;
  tipo: string; // "Fútbol" or "Peña"
  cancelado: boolean;
  Asistencia: {
    PersonaId: number;
    presente: boolean;
    Persona: Persona;
  }[];
}

/* ===================== MODAL: Crear evento + checklist de asistentes ===================== */
const CreateAssignModal = ({
  isOpen,
  onClose,
  title,
  description,
  inputValue,
  setInputValue,
  tipo,
  setTipo,
  personas,
  onSubmit, // (selectedIds: number[]) => Promise<void>
  confirmText = "Crear evento",
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  inputValue: string;
  setInputValue: (v: string) => void;
  tipo: string;
  setTipo: (v: string) => void;
  personas: Persona[]; // lista de jugadores
  onSubmit: (selectedIds: number[]) => Promise<void>;
  confirmText?: string;
}) => {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);
  // Mientras el calendario está abierto se oculta el checklist: ocupan el mismo
  // lugar, así el modal no crece de golpe al elegir otra fecha.
  const [eligiendoFecha, setEligiendoFecha] = useState(false);

  useEffect(() => {
    // Por defecto: ningún jugador seleccionado
    setSelectedIds([]);
  }, [isOpen]);

useEffect(() => {
  if (!isOpen) return;

  setEligiendoFecha(false);

  // En modo crear inicializamos la fecha del padre con el último jueves.
  // En modo edición el valor que ya trae es el bueno.
  if (!inputValue || !inputValue.trim()) {
    setInputValue(format(getLastThursday(), "dd/MM/yyyy"));
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [isOpen]); // solo cuando el modal se abre

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    if (isOpen) window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  const handleToggle = (id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };


  const handleSubmit = async () => {
    if (!inputValue.trim()) {
      // Mensaje simple (el parent puede validar mejor)
      alert("La fecha del evento es obligatoria.");
      return;
    }
    try {
      setSubmitting(true);
      await onSubmit(selectedIds);
    } catch (err) {
      console.error(err);
      alert("Error al crear el evento.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{
          backgroundColor: "rgba(2, 6, 23, 0.55)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
        }}
      >
        <motion.div
          className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl border border-gray-100 bg-white p-4 shadow-2xl dark:border-white/10 dark:bg-navy-800 sm:p-6"
          initial={{ opacity: 0, scale: 0.96, y: -8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.98, y: -6 }}
          transition={{ type: "spring", stiffness: 300, damping: 28 }}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <div className="mb-5">
            <h3 className="text-[19px] font-semibold text-gray-900 dark:text-white">
              {title}
            </h3>
            {description ? (
              <p className="mt-1 text-sm leading-relaxed text-gray-700 dark:text-gray-400">
                {description}
              </p>
            ) : null}
          </div>


        <div className="flex flex-col gap-4">

        {/* Fecha */}
        <SelectorFecha
          value={inputValue}
          onChange={setInputValue}
          onCalendarioChange={setEligiendoFecha}
        />

          {/* Tipo */}
          <div className="w-full">
          <label className="mb-1.5 block text-[13px] font-medium text-gray-900 dark:text-gray-200">
            Tipo
          </label>
          <Select
            unstyled
            value={eventTypeOptions.find((o) => o.value === tipo)}
            isMulti={false}
            isSearchable={false}
            onChange={(v: SingleValue<EventTypeOption>) =>
              setTipo(v?.value ?? "")
            }
            options={eventTypeOptions}
            classNames={selectClassNames<EventTypeOption>()}
          />
          </div>

        </div>

          {/* Checklist: personas */}
          <div className={`mb-4 ${eligiendoFecha ? "hidden" : ""}`}>
            <div className="mb-2 mt-5 flex items-center justify-between gap-3">
              <label className="text-[13px] font-medium text-gray-900 dark:text-gray-200">
                Marcar asistentes
              </label>
              <Badge tone={selectedIds.length > 0 ? "brand" : "neutral"}>
                {selectedIds.length} seleccionados
              </Badge>
            </div>

            <div className="max-h-[280px] overflow-auto rounded-card border border-gray-200/80 bg-gray-50/60 p-2 dark:border-white/10 dark:bg-navy-900">
              {personas && personas.length > 0 ? (
                <motion.div
                  variants={attendeeContainer}
                  initial="hidden"
                  animate="show"
                  className="grid grid-cols-2 gap-2 sm:grid-cols-3"
                >
                  {personas.map((p) => {
                    const checked = selectedIds.includes(p.id);
                    return (
                      <motion.button
                        key={p.id}
                        type="button"
                        variants={attendeeItem}
                        whileTap={{ scale: 0.96 }}
                        onClick={() => handleToggle(p.id)}
                        className={`flex w-full items-center gap-2 rounded-control border px-3 py-2 text-left text-[13px] font-semibold transition-colors duration-150 ${
                          checked
                            ? "border-brand-500 bg-brand-500 text-white"
                            : "border-gray-200/80 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:text-gray-200 dark:hover:bg-navy-700"
                        }`}
                      >
                        <span
                          className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[6px] border transition-colors ${
                            checked
                              ? "border-transparent bg-white/25 text-white"
                              : "border-gray-300 text-transparent dark:border-white/30"
                          }`}
                        >
                          <FaCheck className="h-2.5 w-2.5" />
                        </span>
                        <span className="truncate">{p.nombre}</span>
                      </motion.button>
                    );
                  })}
                </motion.div>
              ) : (
                <p className="p-3 text-sm text-gray-700 dark:text-gray-400">
                  No hay jugadores disponibles
                </p>
              )}
            </div>
          </div>

          {/* Acciones */}
          <div className="mt-6 flex flex-col-reverse gap-2 border-t border-gray-200/80 pt-5 dark:border-white/10 sm:flex-row sm:justify-end">
            <Button variant="secondary" onClick={onClose} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleSubmit} loading={submitting}>
              {confirmText}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};


/**
 * Al abrir el home siendo admin, el modal de "Crear Evento" se abre solo.
 * El flag vive fuera del componente a propósito: así se abre una sola vez por carga
 * de la app y no cada vez que se vuelve al home navegando por el menú.
 */
let autoCreateModalShown = false;

/**
 * Orden que llega desde el panel.
 *
 * `nonce` existe para que dos pedidos iguales seguidos (tocar dos veces el
 * mismo botón) se distingan entre sí y el efecto vuelva a correr.
 */
export interface AccionEventos {
  nonce: number;
  tipo: "crear" | "expandir";
  eventoId?: number;
}

/** ========== MAIN COMPONENT ========== */
const AsistenciaABM = ({
  onAsistenciaSaved,
  accion,
}: {
  onAsistenciaSaved?: () => void; // make it optional or required, your choice
  /** Acción pedida desde la franja del último jueves. */
  accion?: AccionEventos | null;
}) => {
  const [eventos, setEventos] = useState<Asistencia[]>([]);
  const [filteredEventos, setFilteredEventos] = useState<Asistencia[]>([]);

  const { isLoggedIn, user } = useContext(AuthContext);
  const isAdmin = user?.username === "admin";

  // 1) Default "tipo" to "Fútbol" => user sees Fútbol as preselected
  const [nuevoEvento, setNuevoEvento] = useState("");
  const [tipo, setTipo] = useState("Fútbol"); 
  const [editEvento, setEditEvento] = useState<string | null>(null);

  // Personas & expanded
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [expandedEvento, setExpandedEvento] = useState<number | null>(null);

  // Loading states
  const [loadingEventos, setLoadingEventos] = useState(false);
  // `loadingEventos` arranca en false, así que no alcanza para saber si la
  // lista ya llegó. Este flag pasa a true sólo cuando el fetch salió bien.
  const [eventosCargados, setEventosCargados] = useState(false);
  // arranca en true: se hace el fetch apenas monta el componente
  const [loadingPersonas, setLoadingPersonas] = useState(true);

  // == For date-based search ==
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearchCalendar, setShowSearchCalendar] = useState(false);
  const searchCalendarRef = useRef<HTMLDivElement>(null);

  // React Table pagination limit per page
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 9 });

  // === Modal State ===
  const [isModalOpen, setIsModalOpen] = useState(false);

  // =========== FETCHING DATA ===========
  const fetchEventos = async () => {
    try {
      setLoadingEventos(true);
      const res = await axiosInstance.get("/eventos");
      // Sort events so the latest date appears first
      const sortedData = res.data.sort(
        (a: Asistencia, b: Asistencia) =>
          new Date(b.fecha).getTime() - new Date(a.fecha).getTime()
      );

      setEventos(sortedData);
      setFilteredEventos(sortedData);
      setEventosCargados(true);
    } catch (error) {
      console.error("Error al obtener los eventos:", error);
    } finally {
      setLoadingEventos(false);
    }
  };

  const fetchPersonas = async () => {
    try {
      setLoadingPersonas(true);
      const res = await axiosInstance.get("/personas");
      setPersonas(res.data);
    } catch (error) {
      console.error("Error al obtener las personas:", error);
    } finally {
      setLoadingPersonas(false);
    }
  };

  useEffect(() => {
    fetchEventos();
    fetchPersonas();
  }, []);

  // Si sos admin, al entrar al home abrimos directo el modal de crear evento.
  //
  // Dos condiciones antes de abrir:
  //  - que las personas hayan cargado, para que el checklist de asistentes ya
  //    esté listo cuando aparece el modal;
  //  - que la lista de eventos haya cargado *bien*, porque sin ella no podemos
  //    saber si el jueves ya está cargado y abrir a ciegas invita a duplicar.
  useEffect(() => {
    if (autoCreateModalShown) return;
    if (!isLoggedIn || !isAdmin || loadingPersonas || !eventosCargados) return;

    // La decisión se toma una sola vez por carga de la app, se abra o no.
    autoCreateModalShown = true;

    // Si el jueves pasado ya tiene un evento cargado, no hay nada para crear.
    if (hayEventoElUltimoJueves(eventos)) return;

    setNuevoEvento("");
    setTipo("Fútbol");
    setEditEvento(null);
    setIsModalOpen(true);
  }, [isLoggedIn, isAdmin, loadingPersonas, eventosCargados, eventos]);

  /**
   * La franja del último jueves no abre su propio modal: le pide a esta tarjeta
   * que abra el suyo, que es el que tiene el checklist de asistentes. Así el
   * flujo de crear evento sigue existiendo una sola vez en la app.
   */
  useEffect(() => {
    if (!accion) return;

    if (accion.tipo === "crear") {
      setNuevoEvento("");
      setTipo("Fútbol");
      setEditEvento(null);
      setIsModalOpen(true);
      return;
    }

    if (accion.tipo === "expandir" && accion.eventoId) {
      setExpandedEvento(accion.eventoId);

      /*
       * El scroll arranca ya, sin esperar a que la fila termine de abrirse:
       * `irAlElemento` vuelve a medir el destino en cada frame, así que la
       * animación de despliegue puede correr en paralelo sin desviarlo.
       *
       * La fila ya está en el DOM (sólo cambia de alto), salvo que caiga en otra
       * página del listado; ahí no hay adónde ir y queda expandida para cuando
       * se navegue hasta ella.
       */
      const el = document.getElementById(`evento-${accion.eventoId}`);
      // Lo que devuelve corta el viaje: pasa a ser el cleanup del efecto.
      if (el) return irAlElemento(el);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accion?.nonce]);

  // =========== CRUD LOGIC =============

  // CREATE new event
// loading del modal cuando crea+asigna
const [modalSubmitting, setModalSubmitting] = useState(false);

const crearEventoYAsignar = async (selectedIds: number[]) => {
  if (!nuevoEvento.trim()) {
    toast.error("La fecha del evento es obligatoria.");
    return;
  }
  try {
    setModalSubmitting(true);
    const parsedDate = parse(nuevoEvento, "dd/MM/yyyy", new Date());
    const formattedDate = formatISO(parsedDate, { representation: "complete" });

    // 1) Crear evento
    const res = await axiosInstance.post("/eventos", {
      fecha: formattedDate,
      tipo,
    });

    // ideal: el backend devuelve el evento creado con id
    const createdEvent = res?.data;
    // 2) Refrescar eventos (para tener datos actualizados)
    await fetchEventos();

    // 3) Determinar id del evento nuevo
    let newId: number | undefined = createdEvent?.id;
    if (!newId) {
      // fallback: buscar por fecha+tipo (puede fallar si hay duplicados)
      const match = eventos.find(
        (e) =>
          new Date(e.fecha).toISOString().slice(0, 10) ===
            new Date(formattedDate).toISOString().slice(0, 10) && e.tipo === tipo
      );
      newId = match?.id;
    }

    if (!newId) {
      toast.info("Evento creado. Asigná asistentes desde la lista de eventos.");
      setIsModalOpen(false);
      // notify parent so it can refresh the list too
      if (typeof onAsistenciaSaved === "function") {
        try {
          onAsistenciaSaved();
        } catch (e) {
          console.warn("onAsistenciaSaved callback failed:", e);
        }
      }
      return;
    }

    // 4) Preparar asistencias (solo los marcados = presente true)
    const asistenciasPayload = selectedIds.map((pid) => ({ PersonaId: pid, presente: true }));

    // 5) Actualizar asistencias vía endpoint existente
    await axiosInstance.put(`/eventos/${newId}/actualizar-asistencias`, {
      asistencias: asistenciasPayload,
    });

    toast.success("Evento creado y asistentes asignados.");

    // 6) Abrir y scrollear a la tarjeta del evento
    setIsModalOpen(false);
    setExpandedEvento(newId);
    // La espera es para que el modal termine de cerrarse; el desvío que traía
    // este scroll es el mismo que el de la franja del jueves, así que va por el
    // mismo camino.
    setTimeout(() => {
      const el = document.getElementById(`evento-${newId}`);
      if (el) irAlElemento(el);
    }, 200);

    // 7) Refrescar para mostrar asistencias en UI
    await fetchEventos();

    // 8) Notificar al padre que hay cambios (esto hace que la otra sección refresque)
    if (typeof onAsistenciaSaved === "function") {
      try {
        onAsistenciaSaved();
      } catch (e) {
      // no bloquear si el callback falla
        console.warn("onAsistenciaSaved callback failed:", e);
      }
    }
  } catch (error) {
    console.error("Error creating+assigning event:", error);
    toast.error("Ocurrió un error al crear o asignar asistentes.");
  } finally {
    setModalSubmitting(false);
  }
};


  // UPDATE existing event
  const actualizarEvento = async () => {
    if (!nuevoEvento.trim()) {
      toast.error("La fecha del evento es obligatoria.");
      return;
    }
    try {
      const parsedDate = parse(nuevoEvento, "dd/MM/yyyy", new Date());
      const formattedDate = formatISO(parsedDate, { representation: "complete" });

      await axiosInstance.put(`/eventos/${editEvento}`, {
        fecha: formattedDate,
        tipo,
      });
      setNuevoEvento("");
      setEditEvento(null);
      setIsModalOpen(false);
      fetchEventos();
    } catch (error) {
      console.error("Error al actualizar el evento:", error);
    }
  };

  // CANCEL / REACTIVATE
  const toggleCancelEvent = async (eventoId: number, cancelado: boolean) => {
    try {
      await axiosInstance.put(`/eventos/${eventoId}/cancelar`, {
        cancelado: !cancelado,
      });
      fetchEventos();
    } catch (error) {
      console.error("Error al cancelar/revertir el evento:", error);
    }
  };

  // ATTENDANCE
  const actualizarAsistencias = async (
    idEvento: number,
    asistencias: { PersonaId: number; presente: boolean }[]
  ) => {
    try {
      await axiosInstance.put(
        `/eventos/${idEvento}/actualizar-asistencias`,
        { asistencias }
      );
      toast.success("Asistencias guardadas con éxito.");

      // <-- call parent's callback here
      if (onAsistenciaSaved) {
        onAsistenciaSaved();
      }
    } catch (error) {
      console.error("Error al actualizar asistencias:", error);
      toast.error("Hubo un error al guardar las asistencias.");
    }
  };

  // =========== EDIT EVENT HELPER =========== 
  const handleEditEvento = (evento: Asistencia) => {
    setEditEvento(String(evento.id));
    // Convert existing date to dd/MM/yyyy
    const dateObj = new Date(evento.fecha);
    const dateStr = format(dateObj, "dd/MM/yyyy");
    setNuevoEvento(dateStr);
    setTipo(evento.tipo);
    setIsModalOpen(true);
  };

  // =========== SEARCH BY DATE ===========
  const handleSearchDateChange = (selectedDate: Date) => {
    // Format the selected date to dd/MM/yyyy
    const formattedSearchDate = format(selectedDate, "dd/MM/yyyy");
    setSearchTerm(formattedSearchDate);
    setShowSearchCalendar(false);

    // Filter events that match this exact date
    const filtered = eventos.filter((evento) => {
      const eventFormatted = format(new Date(evento.fecha), "dd/MM/yyyy");
      return eventFormatted === formattedSearchDate;
    });
    setFilteredEventos(filtered);

    // Reset to first page
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  // Clear filter (Ver Todos)
  const handleClearFilter = () => {
    setSearchTerm("");
    setFilteredEventos(eventos);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  // =========== EXPANSION LOGIC ===========
  const toggleExpandEvento = (id: number) => {
    setExpandedEvento(expandedEvento === id ? null : id);
  };

  // =========== REACT TABLE SETUP ===========
  const columnHelper = createColumnHelper<Asistencia>();
  const columns: ColumnDef<Asistencia, any>[] = [
    columnHelper.accessor("id", {
      header: "ID",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("fecha", {
      header: "Fecha",
      cell: (info) => info.getValue(),
    }),
  ];

  const table = useReactTable({
    data: filteredEventos,
    columns,
    state: { pagination },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  // Get current page
  const currentEvents = table
    .getRowModel()
    .rows.map((row) => row.original) as Asistencia[];

  // ========== CLOSE SEARCH CALENDAR ON OUTSIDE CLICK ========== 
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        searchCalendarRef.current &&
        !searchCalendarRef.current.contains(e.target as Node)
      ) {
        setShowSearchCalendar(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
      <Card extra="select-none h-full min-h-[560px] w-full p-5 text-left sm:p-6">
        {/* HEADER */}
        <SectionHeader
          title="Eventos"
          description="Partidos y peñas registrados, con sus asistentes."
          actions={
            <>
          {isLoggedIn && (
            <Button
              variant="soft"
              size="icon"
              aria-label="Crear evento"
              onClick={() => {
                setNuevoEvento("");
                setTipo("Fútbol"); // default again if user creates new
                setEditEvento(null);
                setIsModalOpen(true);
              }}
            >
              <TbCalendarPlus className="h-[18px] w-[18px]" />
            </Button>
          )}

          {!isLoggedIn && (
            <div className="relative" ref={searchCalendarRef}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowSearchCalendar((prev) => !prev)}
                rightIcon={<CalendarIcon className="h-4 w-4 text-gray-600" />}
              >
                {searchTerm || "Buscar por fecha"}
              </Button>

              {showSearchCalendar && (
                <div className="calendar_mobile absolute right-0 z-10 mt-2 rounded-card border border-gray-200/80 bg-white p-3 shadow-pop dark:border-white/10 dark:bg-navy-800">
                  <Calendar onChange={handleSearchDateChange} locale="es-ES" />
                </div>
              )}
            </div>
          )}
            </>
          }
        />
        <div className="flex-1">
          {/* Modal for CREATE or EDIT */}
          <CreateAssignModal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setEditEvento(null);
            }}
            title={editEvento ? "Editar Evento" : "Crear Evento"}
            description={editEvento ? "Modifica la fecha/tipo del evento seleccionado" : "Introduce la fecha, tipo y marca asistentes"}
            inputValue={nuevoEvento}
            setInputValue={setNuevoEvento}
            tipo={tipo}
            setTipo={setTipo}
            personas={personas}
            onSubmit={async (selectedIds: number[]) => {
              // si estás editando en vez de crear, podrías manejar actualizarEvento aquí.
              if (editEvento) {
                // si editEvento está seteado, podrías actualizar fecha/tipo y luego actualizar asistencias
                // para simplificar, aquí hacemos la ruta "crear" sólo cuando no hay editEvento
              }
              await crearEventoYAsignar(selectedIds);
            }}
            confirmText={editEvento ? "Guardar cambios" : "Crear evento"}
          />


          {/* Show "Ver Todos" ONLY if a date is selected */}
          {searchTerm && (
            <div className="mb-4 flex items-center gap-2">
              <Badge tone="brand">{searchTerm}</Badge>
              <Button variant="ghost" size="sm" onClick={handleClearFilter}>
                Ver todos
              </Button>
            </div>
          )}

          {loadingEventos ? (
            /* Esqueletos con la forma real de las filas, no un texto suelto */
            <div>
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="mb-2.5 flex items-center justify-between gap-3 rounded-card border border-gray-200/70 bg-white p-4 dark:border-white/5 dark:bg-navy-900"
                >
                  <div className="flex items-center gap-3">
                    <div className="skeleton h-9 w-9 rounded-control" />
                    <div className="space-y-2">
                      <div className="skeleton h-3.5 w-20 rounded-full" />
                      <div className="skeleton h-3 w-14 rounded-full" />
                    </div>
                  </div>
                  <div className="skeleton h-6 w-24 rounded-full" />
                </div>
              ))}
            </div>
          ) : currentEvents.length === 0 ? (
            <EmptyState
              icon={<TbCalendarPlus />}
              title={
                searchTerm
                  ? "No hay eventos en esa fecha"
                  : "Todavía no hay eventos"
              }
              description={
                searchTerm
                  ? "Probá con otra fecha o volvé a la lista completa."
                  : isLoggedIn
                  ? "Creá el primero para empezar a registrar asistencias."
                  : "Cuando se cargue el primer evento vas a verlo acá."
              }
              action={
                searchTerm ? (
                  <Button variant="secondary" size="sm" onClick={handleClearFilter}>
                    Ver todos
                  </Button>
                ) : isLoggedIn ? (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      setNuevoEvento("");
                      setTipo("Fútbol");
                      setEditEvento(null);
                      setIsModalOpen(true);
                    }}
                  >
                    Crear evento
                  </Button>
                ) : undefined
              }
            />
          ) : (
            currentEvents.map((evento) => (
              <div
                key={evento.id}
                id={`evento-${evento.id}`}
                /* scroll-mt: la barra superior es sticky y taparía la fila */
                className={`mb-2.5 scroll-mt-24 select-none overflow-hidden rounded-card border bg-white transition-colors duration-150 dark:bg-navy-900 ${
                  expandedEvento === evento.id
                    ? "border-gray-300 dark:border-white/15"
                    : "border-gray-200/80 hover:border-gray-300 dark:border-white/5 dark:hover:border-white/15"
                }`}
              >
                {/* Expand Header */}
                <div
                  className="group flex cursor-pointer items-center justify-between gap-3 p-4 transition-colors hover:bg-gray-50/60 dark:hover:bg-white/5"
                  onClick={() => toggleExpandEvento(evento.id)}
                  aria-expanded={expandedEvento === evento.id}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    {/* El tipo se lee primero por el icono, no por texto */}
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-control text-[17px] ${
                        evento.cancelado
                          ? "bg-gray-50 text-gray-600 dark:bg-white/5 dark:text-gray-500"
                          : "bg-brand-50 text-brand-600 dark:bg-white/10 dark:text-brand-200"
                      }`}
                    >
                      {evento.tipo === "Fútbol" ? <GiSoccerBall /> : <GiBarbecue />}
                    </span>

                    <div className="min-w-0">
                      <h4
                        className={`tabular text-[15px] font-semibold dark:text-white ${
                          evento.cancelado
                            ? "text-gray-600 line-through dark:text-gray-500"
                            : "text-gray-900"
                        }`}
                      >
                        {formatDate(evento.fecha)}
                      </h4>
                      <p className="text-[12px] text-gray-700 dark:text-gray-500">
                        {evento.tipo}
                      </p>
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    {evento.cancelado ? (
                      <Badge tone="danger" icon={<FaInfoCircle />}>
                        Cancelado
                      </Badge>
                    ) : (
                      <Badge tone="neutral" className="hidden sm:inline-flex">
                        {(evento.Asistencia || []).filter((a) => a.presente).length}{" "}
                        asistentes
                      </Badge>
                    )}

                    <motion.span
                      animate={{ rotate: expandedEvento === evento.id ? 180 : 0 }}
                      transition={{ duration: 0.25 }}
                      className="flex text-gray-600 transition-colors group-hover:text-gray-700 dark:text-gray-500"
                    >
                      <MdKeyboardArrowDown className="h-[18px] w-[18px]" />
                    </motion.span>
                  </div>
                </div>

                {/* Expanded Content */}
              <AnimatePresence initial={false}>
                {expandedEvento === evento.id && (
                  <motion.div
                    key="expand"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{
                      height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
                      opacity: { duration: 0.2 },
                    }}
                    className="overflow-hidden"
                  >
                    <div>
                      {/* ASISTENCIAS */}
                      <div className="border-t border-gray-200/80 px-4 pt-4 dark:border-white/10">
                      <motion.ul
                        variants={attendeeContainer}
                        initial="hidden"
                        animate="show"
                        className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3"
                      >
                        {personas.map((persona) => {
                          const asistencias = evento.Asistencia || [];
                          const asistencia = asistencias.find(
                            (a) => a.PersonaId === persona.id
                          );
                          const presente = asistencia?.presente || false;
                          const editable = isLoggedIn && !evento.cancelado;

                          return (
                            <motion.li key={persona.id} variants={attendeeItem}>
                              <motion.button
                                type="button"
                                whileTap={editable ? { scale: 0.96 } : undefined}
                                disabled={!editable}
                                onClick={() => {
                                  if (!editable) return;
                                  const checked = !presente;
                                  const found = asistencias.find((a) => a.PersonaId === persona.id);
                                  if (found) {
                                    found.presente = checked;
                                  } else {
                                    asistencias.push({
                                      PersonaId: persona.id,
                                      presente: checked,
                                      Persona: persona,
                                    });
                                  }
                                  // Update main state (mantiene las referencias compartidas)
                                  const updatedEventos = eventos.map((ev) =>
                                    ev.id === evento.id ? { ...ev, Asistencia: asistencias } : ev
                                  );
                                  setEventos([...updatedEventos]);
                                }}
                                className={`flex w-full items-center gap-2 rounded-control border px-3 py-2 text-left text-[13px] font-semibold transition-colors duration-150 ${
                                  presente
                                    ? "border-brand-500 bg-brand-500 text-white"
                                    : "border-gray-200/80 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:text-gray-200 dark:hover:bg-navy-700"
                                } ${editable ? "cursor-pointer" : "cursor-default opacity-80"}`}
                              >
                                <span
                                  className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[6px] border transition-colors ${
                                    presente
                                      ? "border-transparent bg-white/25 text-white"
                                      : "border-gray-300 text-transparent dark:border-white/30"
                                  }`}
                                >
                                  <FaCheck className="h-2.5 w-2.5" />
                                </span>
                                <span className="truncate">{persona.nombre}</span>
                              </motion.button>
                            </motion.li>
                          );
                        })}
                      </motion.ul>
                      {isLoggedIn && (
                        <div className="mb-4 mt-4 flex justify-end">
                          <Button
                            variant="primary"
                            size="sm"
                            disabled={evento.cancelado}
                            onClick={() =>
                              actualizarAsistencias(evento.id, evento.Asistencia || [])
                            }
                          >
                            Guardar asistencias
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  </motion.div>
                )}
              </AnimatePresence>
              </div>
            ))
          )}
          </div>
          {/* PAGINATION FROM REACT TABLE */}
          <Pagination
            className="mt-auto border-t border-gray-200/70 pt-5 dark:border-white/10"
            pageIndex={table.getState().pagination.pageIndex}
            pageCount={table.getPageCount()}
            canPreviousPage={table.getCanPreviousPage()}
            canNextPage={table.getCanNextPage()}
            onPreviousPage={() => table.previousPage()}
            onNextPage={() => table.nextPage()}
          />
      </Card>
  );
};

export default AsistenciaABM;
